import {
  CurecallButton,
  CurecallInput,
  CurecallModalContainer,
  CurecallPhoneInput,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { HRContactService } from 'src/services/hr-contact.service'
import { THRContact } from 'src/types/hr'
import { getErrorMessage } from 'src/utils/http.util'
import { formatPhoneToInternational } from 'src/utils/phone.util'

type TContactFormValues = {
  phone: string
  firstname: string
  lastname: string
  role: string
  serviceUnit: string
  remoteId: string
}

type TContactFormModalProps = Readonly<{
  contact?: THRContact
  onClose: () => void
}>

export const ContactFormModal = ({ contact, onClose }: TContactFormModalProps) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const isEditing = contact != null

  const methods = useForm<TContactFormValues>({
    defaultValues: {
      phone: formatPhoneToInternational(contact?.phone),
      firstname: contact?.firstname ?? '',
      lastname: contact?.lastname ?? '',
      role: contact?.role ?? '',
      serviceUnit: contact?.serviceUnit ?? '',
      remoteId: contact?.remoteId ?? '',
    },
  })

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    formState: { errors },
  } = methods

  const invalidateContacts = () => queryClient.invalidateQueries({ queryKey: ['hr-contacts', project.id] })

  const createMutation = useMutation({
    mutationFn: (values: TContactFormValues) =>
      HRContactService.createContact(project.id, {
        phone: values.phone,
        firstname: values.firstname || null,
        lastname: values.lastname || null,
        role: values.role || null,
        serviceUnit: values.serviceUnit || null,
        remoteId: values.remoteId || null,
      }),
    onSuccess: () => {
      invalidateContacts()
      onClose()
    },
    onError: (error) => {
      notify({
        title: getErrorMessage(error),
        message: '',
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (values: TContactFormValues) =>
      HRContactService.updateContact(project.id, contact!.id, {
        phone: values.phone,
        firstname: values.firstname || null,
        lastname: values.lastname || null,
        role: values.role || null,
        serviceUnit: values.serviceUnit || null,
        remoteId: values.remoteId || null,
      }),
    onSuccess: () => {
      invalidateContacts()
      onClose()
    },
    onError: (error) => {
      notify({
        title: getErrorMessage(error),
        message: '',
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    },
  })

  const onSubmit = (values: TContactFormValues) => {
    if (isEditing) {
      updateMutation.mutate(values)
    } else {
      createMutation.mutate(values)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      isOpen
      toggle={onClose}
      dialogClassName="c-max-w-lg"
    >
      <div className="c-pt-6 c-px-8 c-pb-4 c-border-b c-border-b-grey-200">
        <h3 className="c-font-nexa c-font-semibold">
          {isEditing ? t('pages.hr.contacts.form.edit-title') : t('pages.hr.contacts.form.create-title')}
        </h3>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="c-px-8 c-py-6 c-flex c-flex-col c-gap-4">
          <CurecallPhoneInput
            {...register('phone', { required: t('common.errors.required') })}
            name="phone"
            label={t('pages.hr.contacts.form.phone')}
            isRequired
            defaultCountry="FR"
            onChange={(phone) => {
              clearErrors('phone')
              setValue('phone', phone ?? '')
            }}
            notValidPhoneErrorMessage={t('pages.hr.contacts.form.phone-invalid')}
            notCompletePhoneNumberErrorMessage={t('pages.hr.contacts.form.phone-incomplete')}
            errorMessage={errors.phone?.message}
          />

          <div className="c-grid c-grid-cols-2 c-gap-4">
            <CurecallInput {...register('firstname')} label={t('pages.hr.contacts.form.firstname')} />
            <CurecallInput {...register('lastname')} label={t('pages.hr.contacts.form.lastname')} />
          </div>

          <CurecallInput {...register('role')} label={t('pages.hr.contacts.form.role')} />

          <CurecallInput {...register('serviceUnit')} label={t('pages.hr.contacts.form.service-unit')} />

          <CurecallInput {...register('remoteId')} label={t('pages.hr.contacts.form.remote-id')} />

          <div className="c-flex c-justify-end c-gap-3 c-pt-2">
            <CurecallButton
              type="button"
              appearance={ECurecallButtonAppearance.SECONDARY}
              size={ECurecallSize.MEDIUM}
              onClick={onClose}
              isDisabled={isPending}
            >
              {t('common.actions.cancel')}
            </CurecallButton>
            <CurecallButton
              type="submit"
              appearance={ECurecallButtonAppearance.PRIMARY}
              size={ECurecallSize.MEDIUM}
              isDisabled={isPending}
            >
              {isEditing
                ? t('pages.hr.contacts.form.submit-edit')
                : t('pages.hr.contacts.form.submit-create')}
            </CurecallButton>
          </div>
        </form>
      </FormProvider>
    </CurecallModalContainer>
  )
}
